import { useContentEditingSafe, type PreviewDevice } from "./ContentEditingContext";
import {
  Pencil, X, RotateCcw, Save, Loader2, AlertTriangle,
  History, ChevronUp, ChevronDown, Eye, EyeOff, Sparkles,
  Undo2, Redo2, Monitor, Tablet, Smartphone, Activity,
  ShoppingCart, BookOpen, Layers, Plus, Trash2, CornerDownRight,
  GripVertical, Camera, Check, ExternalLink,
  ClipboardPaste,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Link } from "./SiteLink";
import { useSiteNavigate } from "./SiteLink";
import { AiWriterModal } from "./AiWriterModal";
import { ContentCompletionMeter } from "./ContentCompletionMeter";
import { useTemplateType } from "./TemplateContext";
import { STORE_MODE_KEY, type StoreMode } from "./useStoreMode";
import { useCustomNav, type MergedNavItem, type CustomNavItem } from "./useCustomNav";
import { FindReplaceModal } from "./FindReplaceModal";
import { usePresence } from "./usePresence";
import { useTemplateId } from "./TemplateContext";
import { useAuthSafe } from "./AuthContext";
import { PresenceAvatars } from "./PresenceAvatars";

/**
 * ContentEditToggle + inline nav editor.
 * Keyboard shortcuts: Ctrl+E, Ctrl+Shift+P, Ctrl+Z, Ctrl+Y, Esc
 */

const DEVICE_OPTIONS: { id: PreviewDevice; icon: typeof Monitor; label: string }[] = [
  { id: "desktop", icon: Monitor, label: "Desktop" },
  { id: "tablet", icon: Tablet, label: "Tablet (768px)" },
  { id: "mobile", icon: Smartphone, label: "Mobile (375px)" },
];

const btnReset = "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

/* ================================================================
   Inline Nav Editor — tree view with inline label editing
   ================================================================ */

const DND_NAV_INLINE = "NAV_INLINE_REORDER";

function InlineNavEditor({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const ctx = useContentEditingSafe();
  const navigate = useSiteNavigate();
  const {
    mergedNav, customItems, addItem, removeItem, toggleHidden,
    reorderNavItem, moveNavToIndex,
    updateItemLabel, updateChildLabel,
    deleteItem, restoreDeletedItem, deletedDefaults,
    suggestions, availableParents,
  } = useCustomNav();

  /** Navigate to a page and close the modal */
  const handleNavigateTo = useCallback((path: string) => {
    if (path.startsWith("http")) {
      window.open(path, "_blank");
    } else {
      navigate(path);
      onClose();
    }
  }, [navigate, onClose]);

  const getContent = ctx?.getContent ?? ((_k: string, d: string) => d);

  /* --- Add form state --- */
  const [showAdd, setShowAdd] = useState(false);
  const [addLabel, setAddLabel] = useState("");
  const [addPath, setAddPath] = useState("");
  const [addParent, setAddParent] = useState(""); // empty = top level
  const [addShowInMenu, setAddShowInMenu] = useState(true);

  const resetAdd = useCallback(() => {
    setShowAdd(false);
    setAddLabel("");
    setAddPath("");
    setAddParent("");
    setAddShowInMenu(true);
  }, []);

  const handleAdd = useCallback(() => {
    if (!addLabel.trim() || !addPath.trim()) return;
    addItem({
      label: addLabel.trim(),
      path: addPath.trim(),
      type: addPath.startsWith("http") ? "external" : "page",
      ...(addParent ? { parentPath: addParent } : {}),
      hideFromMenu: !addShowInMenu,
    });
    resetAdd();
  }, [addLabel, addPath, addParent, addShowInMenu, addItem, resetAdd]);

  /* Start adding a child directly under a parent */
  const startAddChild = useCallback((parentPath: string) => {
    setShowAdd(true);
    setAddParent(parentPath);
    setAddLabel("");
    setAddPath("");
    setAddShowInMenu(true);
  }, []);

  /* Find custom item ID by path (for deletion) */
  const findCustomId = useCallback(
    (path: string, parentPath?: string) => {
      const match = customItems.find((ci) =>
        ci.path === path && (parentPath ? ci.parentPath === parentPath : !ci.parentPath)
      );
      return match?.id;
    },
    [customItems]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col mx-4"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---- Header ---- */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" }}
            >
              <Layers className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base text-gray-900" style={{ fontWeight: 700 }}>
                {"ניהול תפריט ועמודים"}
              </h2>
              <p className="text-xs text-gray-500">
                {"הוסיפו, הסירו, הסתירו או מיינו — לחצו על העיפרון לעריכת שם"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`${btnReset} p-2 rounded-lg hover:bg-gray-100`}
            style={{ color: "#9ca3af" }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ---- Body — tree view ---- */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1.5">
          {mergedNav.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">
              {"אין עמודים. לחצו על הוספת עמוד."}
            </p>
          )}

          {mergedNav.map((item, itemIdx) => {
            const hasChildren = item.children && item.children.length > 0;
            const isCustomTop = !item.isDefault;
            const customTopId = isCustomTop ? findCustomId(item.path) : undefined;
            const isFirst = itemIdx === 0;
            const isLast = itemIdx === mergedNav.length - 1;

            return (
              <DraggableInlineNavRow
                key={item.path}
                item={item}
                index={itemIdx}
                hasChildren={hasChildren}
                isCustomTop={isCustomTop}
                customTopId={customTopId}
                isFirst={isFirst}
                isLast={isLast}
                onToggleHidden={toggleHidden}
                onRemove={removeItem}
                onStartAddChild={startAddChild}
                onReorder={reorderNavItem}
                onMoveToIndex={moveNavToIndex}
                onUpdateLabel={updateItemLabel}
                onUpdateChildLabel={updateChildLabel}
                onDelete={deleteItem}
                customItems={customItems}
                onNavigateTo={handleNavigateTo}
              />
            );
          })}

          {/* ── Deleted pages restore section ── */}
          {deletedDefaults.length > 0 && (
            <div className="mt-3 border border-gray-200 rounded-xl p-3 space-y-1.5" style={{ background: "#fafafa" }}>
              <p className="text-xs text-gray-400 mb-1" style={{ fontWeight: 600 }}>
                {"עמודים שנמחקו:"}
              </p>
              {deletedDefaults.map((d) => (
                <div
                  key={d.path}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white"
                >
                  <Trash2 className="w-3 h-3 shrink-0" style={{ color: "#d1d5db" }} />
                  <span className="flex-1 text-xs text-gray-400 line-through truncate" style={{ fontWeight: 500 }}>
                    {d.label}
                  </span>
                  <span className="text-[10px] text-gray-300 shrink-0" dir="ltr">{d.path}</span>
                  <button
                    type="button"
                    onClick={() => restoreDeletedItem(d.path)}
                    className={`${btnReset} flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-gray-200 hover:bg-purple-50`}
                    style={{ fontWeight: 600, color: "#7c3aed" }}
                  >
                    <RotateCcw className="w-3 h-3" />
                    {"שחזר"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Add form ── */}
          {showAdd && (
            <div
              className="border-2 border-dashed rounded-xl p-4 space-y-3 mt-3"
              style={{ borderColor: "#a78bfa", background: "#f5f3ff" }}
            >
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4" style={{ color: "#7c3aed" }} />
                <span className="text-sm" style={{ fontWeight: 700, color: "#5b21b6" }}>
                  {addParent
                    ? `הוספת תת-עמוד תחת "${getContent(`nav:${addParent}`, availableParents.find((p) => p.path === addParent)?.label ?? addParent)}"`
                    : "הוספת עמוד חדש"}
                </span>
              </div>

              <input
                type="text"
                value={addLabel}
                onChange={(e) => setAddLabel(e.target.value)}
                placeholder={"שם העמוד (למשל: גלריה)"}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                style={{ outline: "none" }}
              />
              <input
                type="text"
                value={addPath}
                onChange={(e) => setAddPath(e.target.value)}
                placeholder={"נתיב (למשל: /gallery) או כתובת URL"}
                dir="ltr"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                style={{ outline: "none" }}
              />

              {/* Parent selector */}
              <div>
                <label className="block text-xs text-gray-500 mb-1" style={{ fontWeight: 500 }}>
                  {"מיקום בתפריט:"}
                </label>
                <select
                  value={addParent}
                  onChange={(e) => setAddParent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  style={{ outline: "none" }}
                >
                  <option value="">
                    {"ראשי (ללא הורה)"}
                  </option>
                  {availableParents.map((p) => (
                    <option key={p.path} value={p.path}>
                      {"↳ "}
                      {getContent(`nav:${p.path}`, p.label)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Show in menu toggle */}
              {!addPath.startsWith("http") && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={addShowInMenu}
                    onChange={(e) => setAddShowInMenu(e.target.checked)}
                    className="w-4 h-4 rounded accent-purple-500"
                  />
                  <span className="text-xs" style={{ fontWeight: 600, color: "#4b5563" }}>
                    הצג בתפריט הניווט
                  </span>
                  {!addShowInMenu && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#fef3c7", color: "#92400e", fontWeight: 500 }}>
                      העמוד ייווצר אך לא יוצג בתפריט
                    </span>
                  )}
                </label>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={!addLabel.trim() || !addPath.trim()}
                  className={`${btnReset} flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm shadow-sm`}
                  style={{
                    fontWeight: 600,
                    background:
                      addLabel.trim() && addPath.trim() ? "#7c3aed" : "#d1d5db",
                    color: "#fff",
                    cursor:
                      addLabel.trim() && addPath.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  <Plus className="w-4 h-4" />
                  {"הוסף"}
                </button>
                <button
                  type="button"
                  onClick={resetAdd}
                  className={`${btnReset} px-4 py-2 rounded-lg text-sm`}
                  style={{ color: "#6b7280" }}
                >
                  {"ביטול"}
                </button>
              </div>
            </div>
          )}

          {/* ── Quick suggestions ── */}
          {suggestions.length > 0 && !showAdd && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400 mb-2">
                {"לחצו להוספה מהירה:"}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.slice(0, 10).map((s) => (
                  <button
                    key={s.path}
                    type="button"
                    onClick={() =>
                      addItem({ label: s.label, path: s.path, type: s.type })
                    }
                    className={`${btnReset} flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs border border-gray-200 bg-gray-50 hover:bg-purple-50`}
                    style={{ color: "#374151" }}
                  >
                    <Plus className="w-3 h-3" style={{ color: "#9ca3af" }} />
                    <span style={{ fontWeight: 500 }}>{s.label}</span>
                    {s.type === "category" && (
                      <span
                        className="text-[9px] px-1 py-0.5 rounded"
                        style={{ background: "#dbeafe", color: "#2563eb", fontWeight: 600 }}
                      >
                        {"קטגוריה"}
                      </span>
                    )}
                    {s.type === "product" && (
                      <span
                        className="text-[9px] px-1 py-0.5 rounded"
                        style={{ background: "#fef3c7", color: "#d97706", fontWeight: 600 }}
                      >
                        {"מוצר"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ---- Footer ---- */}
        <div
          className="flex items-center justify-between px-5 py-3 border-t border-gray-200"
          style={{ background: "#f9fafb", borderRadius: "0 0 1rem 1rem" }}
        >
          <button
            type="button"
            onClick={() => {
              setShowAdd(true);
              setAddParent("");
            }}
            className={`${btnReset} flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm shadow-sm`}
            style={{ fontWeight: 600, background: "#7c3aed", color: "#fff" }}
          >
            <Plus className="w-4 h-4" />
            {"הוספת עמוד חדש"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`${btnReset} px-4 py-2 rounded-lg text-sm`}
            style={{ fontWeight: 600, color: "#374151", background: "#e5e7eb" }}
          >
            {"סגור"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   Main Component — ContentEditToggle
   ================================================================ */

export function ContentEditToggle() {
  const ctx = useContentEditingSafe();
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showNavEditor, setShowNavEditor] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [isCapturingThumb, setIsCapturingThumb] = useState(false);
  const [showUndoHistory, setShowUndoHistory] = useState(false);
  const templateType = useTemplateType();

  if (!ctx) return null;

  const {
    isEditMode, isPreviewMode, toggleEditMode, togglePreviewMode,
    resetAllContent, pendingCount, isSaving, isLoading, saveError,
    undo, redo, canUndo, canRedo, previewDevice, setPreviewDevice,
    getContent, saveContent,
    lastUndoLabel, lastRedoLabel, undoHistory,
  } = ctx;

  const isStore = templateType === "store";
  const currentStoreMode: StoreMode = isStore
    ? (getContent(STORE_MODE_KEY, "ecommerce") === "catalog" ? "catalog" : "ecommerce")
    : "ecommerce";

  const toggleStoreMode = () => {
    saveContent(STORE_MODE_KEY, currentStoreMode === "ecommerce" ? "catalog" : "ecommerce");
  };

  /* Keyboard shortcuts */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const inInput =
        t.isContentEditable ||
        t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.tagName === "SELECT";
      if ((e.ctrlKey || e.metaKey) && e.key === "e") {
        e.preventDefault();
        if (isPreviewMode) togglePreviewMode();
        toggleEditMode();
        return;
      }
      // Ctrl+H — Find & Replace (only in edit mode)
      if ((e.ctrlKey || e.metaKey) && e.key === "h" && isEditMode) {
        e.preventDefault();
        setShowFindReplace((prev) => !prev);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "P") {
        e.preventDefault();
        if (isEditMode || isPreviewMode) togglePreviewMode();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        e.key === "z" &&
        isEditMode &&
        !inInput
      ) {
        e.preventDefault();
        undo();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        isEditMode &&
        !inInput &&
        (e.key === "y" || (e.shiftKey && (e.key === "z" || e.key === "Z")))
      ) {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === "Escape" && !inInput) {
        if (isPreviewMode) {
          e.preventDefault();
          togglePreviewMode();
        } else if (isEditMode) {
          e.preventDefault();
          toggleEditMode();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isEditMode, isPreviewMode, toggleEditMode, togglePreviewMode, undo, redo]);

  const status = (() => {
    if (isSaving)
      return {
        icon: <Loader2 className="w-3 h-3 animate-spin" />,
        text: "שומר...",
        color: "text-amber-300",
      };
    if (pendingCount > 0)
      return {
        icon: <Loader2 className="w-3 h-3 animate-spin" />,
        text: `${pendingCount} שינויים...`,
        color: "text-amber-300",
      };
    if (saveError)
      return {
        icon: <AlertTriangle className="w-3 h-3" />,
        text: "שגיאה",
        color: "text-red-400",
      };
    return {
        icon: <Save className="w-3 h-3" />,
        text: "הכל שמור",
        color: "text-emerald-400",
      };
  })();

  const templateId = useTemplateId();
  const auth = useAuthSafe();
  const presenceOthers = usePresence(
    templateId,
    auth?.user?.username || null,
    auth?.user?.displayName || auth?.user?.username || null,
  );

  return (
    <>
      {/* ===== Preview bar ===== */}
      {isPreviewMode && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 px-3 py-2 rounded-2xl shadow-2xl bg-zap-navy/95 backdrop-blur-sm border border-white/10">
          <div className="flex items-center gap-0.5 bg-white/10 rounded-xl p-0.5">
            {DEVICE_OPTIONS.map((d) => {
              const Icon = d.icon;
              const active = previewDevice === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setPreviewDevice(d.id)}
                  className={`${btnReset} flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${
                    active
                      ? "bg-white text-zap-navy shadow-sm"
                      : "text-white/60 hover:text-white hover:bg-white/10"
                  }`}
                  style={{ fontWeight: active ? 600 : 400 }}
                  title={d.label}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{d.label.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
          <div className="w-px h-6 bg-white/20" />
          <button
            type="button"
            onClick={() => {
              setPreviewDevice("desktop");
              togglePreviewMode();
            }}
            className={`${btnReset} flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs hover:bg-white/15`}
            style={{ fontWeight: 500, color: "#fff" }}
          >
            <EyeOff className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{"יציאה"}</span>
          </button>
        </div>
      )}

      {/* ===== Floating edit button ===== */}
      {!isPreviewMode && (
        <button
          type="button"
          data-tour-step="edit-toggle"
          onClick={toggleEditMode}
          className={`${btnReset} fixed bottom-24 left-6 z-[1000] w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 ${
            isEditMode ? "" : "bg-zap-navy hover:bg-zap-dark"
          }`}
          style={{
            color: "#fff",
            ...(isEditMode
              ? {
                  backgroundColor: "#f59e0b",
                  boxShadow: "0 0 0 4px rgba(252,211,77,0.3)",
                }
              : {}),
          }}
          title={
            isEditMode
              ? "צא ממצב עריכה (Ctrl+E)"
              : "ערוך תוכן (Ctrl+E)"
          }
        >
          {isLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : isEditMode ? (
            <X className="w-6 h-6" />
          ) : (
            <Pencil className="w-5 h-5" />
          )}
        </button>
      )}

      {/* ===== Floating AI button ===== */}
      {isEditMode && !isPreviewMode && (
        <button
          type="button"
          onClick={() => setShowAiModal(true)}
          className={`${btnReset} fixed bottom-24 left-[88px] z-[1000] w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform`}
          style={{
            background: "linear-gradient(135deg, #052b7a 0%, #1a4494 100%)",
            color: "#fff",
          }}
          title={"עוזר AI"}
        >
          <Sparkles className="w-5 h-5" />
        </button>
      )}

      {/* ===== Floating NAV EDITOR button (purple) ===== */}
      {isEditMode && !isPreviewMode && (
        <button
          type="button"
          data-tour-step="nav-editor-btn"
          onClick={() => setShowNavEditor(true)}
          className={`${btnReset} fixed bottom-6 left-[88px] z-[1000] w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform`}
          style={{
            background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
            color: "#fff",
          }}
          title={"ניהול תפריט"}
        >
          <Layers className="w-5 h-5" />
        </button>
      )}

      {/* ===== Top toolbar ===== */}
      {isEditMode && !isPreviewMode && (
        <>
          <div
            data-edit-toolbar
            data-tour-step="edit-toolbar"
            className={`fixed top-0 left-0 right-0 z-[999] py-2 px-4 flex flex-col shadow-2xl transition-transform duration-300 ${
              toolbarCollapsed ? "-translate-y-full" : "translate-y-0"
            }`}
            style={{
              background:
                "linear-gradient(135deg, #031d4e 0%, #0a2d6b 50%, #0f3577 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="flex items-center justify-between text-sm flex-wrap gap-2">
              {/* Left label */}
              <div className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: "rgba(245,158,11,0.25)" }}
                >
                  <Pencil className="w-3.5 h-3.5" style={{ color: "#fbbf24" }} />
                </div>
                <span
                  className="hidden sm:inline"
                  style={{ fontWeight: 500, color: "rgba(255,255,255,0.85)" }}
                >
                  {"מצב עריכת תוכן"}
                </span>
                <span
                  className="sm:hidden text-xs"
                  style={{ fontWeight: 500, color: "rgba(255,255,255,0.85)" }}
                >
                  {"עריכה"}
                </span>
              </div>

              {/* Right buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Store mode */}
                {isStore && (
                  <>
                    <button
                      type="button"
                      onClick={toggleStoreMode}
                      className={`${btnReset} flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs`}
                      style={{
                        fontWeight: 600,
                        background:
                          currentStoreMode === "catalog"
                            ? "rgba(99,102,241,0.25)"
                            : "rgba(16,185,129,0.25)",
                        color:
                          currentStoreMode === "catalog" ? "#a5b4fc" : "#6ee7b7",
                        border:
                          currentStoreMode === "catalog"
                            ? "1px solid rgba(99,102,241,0.4)"
                            : "1px solid rgba(16,185,129,0.4)",
                      }}
                    >
                      {currentStoreMode === "catalog" ? (
                        <>
                          <BookOpen className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">
                            {"קטלוג"}
                          </span>
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">
                            {"חנות"}
                          </span>
                        </>
                      )}
                    </button>
                    <div
                      className="w-px h-6 mx-0.5 hidden sm:block"
                      style={{ background: "rgba(255,255,255,0.15)" }}
                    />
                  </>
                )}

                {/* Status */}
                <div
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${status.color}`}
                  style={{ background: "rgba(255,255,255,0.08)" }}
                >
                  {status.icon}
                  {status.text}
                </div>
                <ContentCompletionMeter />
                <div
                  className="w-px h-6 mx-0.5 hidden sm:block"
                  style={{ background: "rgba(255,255,255,0.15)" }}
                />

                {/* Undo/Redo with history dropdown */}
                <div
                  className="relative flex items-center gap-0.5 rounded-lg p-0.5"
                  style={{ background: "rgba(255,255,255,0.08)" }}
                >
                  <button
                    type="button"
                    disabled={!canUndo}
                    onClick={canUndo ? undo : undefined}
                    className={`${btnReset} flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs`}
                    style={{
                      color: canUndo ? "#fff" : "rgba(255,255,255,0.25)",
                      background: canUndo
                        ? "rgba(255,255,255,0.12)"
                        : "transparent",
                      cursor: canUndo ? "pointer" : "not-allowed",
                    }}
                    title={canUndo && lastUndoLabel ? `ביטול: ${lastUndoLabel} (Ctrl+Z)` : "ביטול (Ctrl+Z)"}
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{"ביטול"}</span>
                  </button>
                  <button
                    type="button"
                    disabled={!canRedo}
                    onClick={canRedo ? redo : undefined}
                    className={`${btnReset} flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs`}
                    style={{
                      color: canRedo ? "#fff" : "rgba(255,255,255,0.25)",
                      background: canRedo
                        ? "rgba(255,255,255,0.12)"
                        : "transparent",
                      cursor: canRedo ? "pointer" : "not-allowed",
                    }}
                    title={canRedo && lastRedoLabel ? `שחזור: ${lastRedoLabel} (Ctrl+Y)` : "שחזור (Ctrl+Y)"}
                  >
                    <Redo2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{"שחזור"}</span>
                  </button>
                  {undoHistory.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowUndoHistory(!showUndoHistory)}
                      className={`${btnReset} px-1 py-1.5 rounded-md text-xs`}
                      style={{
                        color: "rgba(255,255,255,0.5)",
                        background: showUndoHistory ? "rgba(255,255,255,0.15)" : "transparent",
                      }}
                      title="היסטוריית עריכה"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  )}
                  {showUndoHistory && undoHistory.length > 0 && (
                    <div
                      className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 px-1 min-w-[220px] max-h-[280px] overflow-y-auto z-[1001]"
                      dir="rtl"
                    >
                      <p className="text-[10px] text-gray-400 px-3 pb-1 mb-1 border-b border-gray-100" style={{ fontWeight: 600 }}>
                        {"היסטוריית עריכה"}
                      </p>
                      {undoHistory.map((entry, i) => {
                        const timeAgo = Math.round((Date.now() - entry.timestamp) / 1000);
                        const timeStr = timeAgo < 60 ? `${timeAgo}ש` : timeAgo < 3600 ? `${Math.round(timeAgo / 60)}ד` : `${Math.round(timeAgo / 3600)}שע`;
                        return (
                          <div
                            key={`${entry.timestamp}-${i}`}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-xs"
                          >
                            <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                            <span className="flex-1 text-gray-700" style={{ fontWeight: 500 }}>{entry.label}</span>
                            <span className="text-gray-400 text-[10px] shrink-0" dir="ltr">{timeStr}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div
                  className="w-px h-6 mx-0.5 hidden sm:block"
                  style={{ background: "rgba(255,255,255,0.15)" }}
                />

                {/* AI Writer */}
                <button
                  type="button"
                  onClick={() => setShowAiModal(true)}
                  className={`${btnReset} flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs shadow-lg`}
                  style={{
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
                    color: "#fff",
                  }}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{"עוזר AI"}</span>
                  <span className="sm:hidden">AI</span>
                </button>

                {/* NAV EDITOR */}
                <button
                  type="button"
                  onClick={() => setShowNavEditor(true)}
                  className={`${btnReset} flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs shadow-lg`}
                  style={{
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                    color: "#fff",
                  }}
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{"תפריט"}</span>
                  <span className="sm:hidden">
                    <Layers className="w-3 h-3" />
                  </span>
                </button>

                {/* Preview */}
                <button
                  type="button"
                  onClick={togglePreviewMode}
                  className={`${btnReset} flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs`}
                  style={{
                    fontWeight: 500,
                    color: "#fff",
                    background: "rgba(255,255,255,0.1)",
                  }}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{"תצוגה"}</span>
                </button>

                {/* Capture Thumbnail */}
                <button
                  type="button"
                  disabled={isCapturingThumb}
                  onClick={async () => {
                    setIsCapturingThumb(true);
                    try {
                      const { captureThumbnailAndSave } = await import("../../utils/thumbnailApi");
                      const result = await captureThumbnailAndSave(templateId);
                      if (result) {
                        const { toast } = await import("sonner");
                        toast.success("תמונה מקדימה נשמרה");
                      }
                    } catch {
                      console.error("[ContentEditToggle] Thumbnail capture failed");
                    } finally {
                      setIsCapturingThumb(false);
                    }
                  }}
                  className={`${btnReset} flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs`}
                  style={{
                    fontWeight: 500,
                    color: isCapturingThumb ? "rgba(255,255,255,0.4)" : "#fff",
                    background: "rgba(255,255,255,0.1)",
                    cursor: isCapturingThumb ? "wait" : "pointer",
                  }}
                  title={"צילום תמונה מקדימה"}
                >
                  {isCapturingThumb ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Camera className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">
                    {isCapturingThumb ? "מצלם..." : "תמונה"}
                  </span>
                </button>

                <div
                  className="w-px h-6 mx-0.5 hidden sm:block"
                  style={{ background: "rgba(255,255,255,0.15)" }}
                />

                {/* History / Log / Reset */}
                <div
                  className="flex items-center gap-0.5 rounded-lg p-0.5"
                  style={{ background: "rgba(255,255,255,0.08)" }}
                >
                  <Link
                    to="/change-history"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs no-underline"
                    style={{
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.8)",
                      background: "rgba(255,255,255,0.1)",
                    }}
                  >
                    <History className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">
                      {"היסטוריה"}
                    </span>
                  </Link>
                  <Link
                    to="/activity-log"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs no-underline"
                    style={{
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.8)",
                      background: "rgba(255,255,255,0.1)",
                    }}
                  >
                    <Activity className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{"יומן"}</span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShowConfirmReset(true)}
                    className={`${btnReset} flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs`}
                    style={{ fontWeight: 500, color: "rgba(255,255,255,0.7)" }}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{"אפס"}</span>
                  </button>
                </div>
                <div
                  className="w-px h-6 mx-0.5 hidden sm:block"
                  style={{ background: "rgba(255,255,255,0.15)" }}
                />

                {/* Close / Collapse */}
                <button
                  type="button"
                  onClick={toggleEditMode}
                  className={`${btnReset} px-3.5 py-1.5 rounded-lg text-xs shadow-lg`}
                  style={{ fontWeight: 600, color: "#fff", background: "#dc2626" }}
                >
                  {"סיום עריכה ✕"}
                </button>
                <button
                  type="button"
                  onClick={() => setToolbarCollapsed(true)}
                  className={`${btnReset} px-2 py-1.5 rounded-lg text-xs`}
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    background: "rgba(255,255,255,0.08)",
                  }}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            </div>
            {saveError && (
              <div
                className="mt-1.5 flex items-center gap-2 text-xs rounded-lg px-3 py-1.5"
                style={{ background: "rgba(239,68,68,0.25)", color: "#fca5a5" }}
              >
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span>{saveError}</span>
              </div>
            )}
          </div>

          {toolbarCollapsed && (
            <button
              type="button"
              onClick={() => setToolbarCollapsed(false)}
              className={`${btnReset} fixed top-0 left-1/2 -translate-x-1/2 z-[999] flex items-center gap-1.5 px-4 py-1.5 rounded-b-lg text-xs shadow-lg`}
              style={{
                background:
                  "linear-gradient(135deg, #031d4e 0%, #0a2d6b 100%)",
                color: "#fff",
                borderBottom: "2px solid #f59e0b",
              }}
            >
              <ChevronDown className="w-3.5 h-3.5" />
              {"סרגל עריכה"}
            </button>
          )}
        </>
      )}

      {/* AI Modal */}
      <AiWriterModal isOpen={showAiModal} onClose={() => setShowAiModal(false)} />

      {/* Nav Editor Modal */}
      <InlineNavEditor
        isOpen={showNavEditor}
        onClose={() => setShowNavEditor(false)}
      />

      {/* Find & Replace Modal */}
      <FindReplaceModal
        isOpen={showFindReplace}
        onClose={() => setShowFindReplace(false)}
      />

      {/* Confirm reset */}
      {showConfirmReset && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50"
          onClick={() => setShowConfirmReset(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-lg text-zap-navy mb-3"
              style={{ fontWeight: 700 }}
            >
              {"איפוס כל התוכן"}
            </h3>
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              {"האם אתה בטוח? פעולה זו תמחק את כל השינויים."}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowConfirmReset(false)}
                className={`${btnReset} px-4 py-2 text-sm rounded-lg`}
                style={{ color: "#6b7280" }}
              >
                {"ביטול"}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetAllContent();
                  setShowConfirmReset(false);
                }}
                className={`${btnReset} px-4 py-2 text-sm rounded-lg`}
                style={{ background: "#dc2626", color: "#fff" }}
              >
                {"אפס הכל"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Presence avatars */}
      {presenceOthers.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[998] flex items-center gap-2 px-3 py-2 rounded-2xl shadow-2xl bg-zap-navy/95 backdrop-blur-sm border border-white/10"
        >
          <PresenceAvatars users={presenceOthers} size="md" />
          <span className="text-[11px] text-white/70" style={{ fontWeight: 500 }}>
            {"עורכים עכשיו"}
          </span>
        </div>
      )}
    </>
  );
}

/* ================================================================
   Inline Editable Label — click pencil to edit, blur/Enter to save
   ================================================================ */

function InlineEditableLabel({
  value,
  onSave,
}: {
  value: string;
  onSave: (newValue: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    }
    setEditing(false);
  }, [draft, value, onSave]);

  const cancel = useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  if (editing) {
    return (
      <div className="flex items-center gap-1 flex-1 min-w-0">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") cancel();
          }}
          className="flex-1 min-w-0 px-2 py-1 text-sm border border-purple-300 rounded-lg bg-purple-50/50 outline-none"
          style={{ fontWeight: 600 }}
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          className={`${btnReset} p-1 rounded-md hover:bg-green-50`}
          style={{ color: "#22c55e" }}
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-1 min-w-0 group/label">
      <span
        className="block text-sm text-gray-900 truncate"
        style={{ fontWeight: 600 }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => { setDraft(value); setEditing(true); }}
        className={`${btnReset} p-1 rounded-md opacity-0 group-hover/label:opacity-100 hover:bg-purple-50`}
        style={{ color: "#a78bfa", transition: "opacity 0.15s" }}
        title="עריכת שם"
      >
        <Pencil className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ================================================================
   Draggable Inline Nav Row — with DnD + arrow fallback + inline edit
   ================================================================ */

interface DraggableInlineNavRowProps {
  item: MergedNavItem;
  index: number;
  hasChildren: boolean;
  isCustomTop: boolean;
  customTopId: string | undefined;
  isFirst: boolean;
  isLast: boolean;
  onToggleHidden: (path: string) => void;
  onRemove: (id: string) => void;
  onStartAddChild: (parentPath: string) => void;
  onReorder: (path: string, direction: "up" | "down") => void;
  onMoveToIndex: (fromPath: string, toIndex: number) => void;
  onUpdateLabel: (path: string, newLabel: string) => void;
  onUpdateChildLabel: (childPath: string, parentPath: string, newLabel: string) => void;
  onDelete: (path: string) => void;
  customItems: CustomNavItem[];
  onNavigateTo: (path: string) => void;
}

function DraggableInlineNavRow({
  item,
  index,
  hasChildren,
  isCustomTop,
  customTopId,
  isFirst,
  isLast,
  onToggleHidden,
  onRemove,
  onStartAddChild,
  onReorder,
  onMoveToIndex,
  onUpdateLabel,
  onUpdateChildLabel,
  onDelete,
  customItems,
  onNavigateTo,
}: DraggableInlineNavRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);

  // Drag source
  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: DND_NAV_INLINE,
      item: { path: item.path, index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [item.path, index],
  );

  // Drop target
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DND_NAV_INLINE,
      drop: (dragItem: { path: string; index: number }) => {
        if (dragItem.path !== item.path) {
          onMoveToIndex(dragItem.path, index);
        }
      },
      canDrop: (dragItem: { path: string }) => dragItem.path !== item.path,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [item.path, index, onMoveToIndex],
  );

  // Combine drag-preview + drop on the wrapper div
  drop(dragPreview(rowRef));

  const showDropLine = isOver && canDrop;

  return (
    <div
      ref={rowRef}
      className="space-y-0"
      style={{ opacity: isDragging ? 0.35 : 1 }}
    >
      {/* Drop indicator line */}
      {showDropLine && (
        <div className="h-[3px] rounded-full mx-2 mb-1" style={{ background: "#7c3aed" }} />
      )}

      {/* ── Parent row ── */}
      {item.isHidden ? (
        /* ── Hidden item: ultra-compact restore row ── */
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-gray-200 bg-gray-50/60">
          <EyeOff className="w-3 h-3 shrink-0" style={{ color: "#d1d5db" }} />
          <span className="flex-1 min-w-0 text-xs text-gray-400 truncate line-through">
            {item.label}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#fef3c7", color: "#92400e", fontWeight: 600 }}>
            {"מוסתר"}
          </span>
          {/* Navigate to hidden page */}
          <button
            type="button"
            onClick={() => onNavigateTo(item.path)}
            className={`${btnReset} flex items-center gap-1 px-2 py-1 rounded-md text-[11px] hover:bg-blue-50`}
            style={{ fontWeight: 500, color: "#3b82f6" }}
            title={"עבור לעמוד"}
          >
            <ExternalLink className="w-3 h-3" />
            {"צפה"}
          </button>
          <button
            type="button"
            onClick={() => onToggleHidden(item.path)}
            className={`${btnReset} flex items-center gap-1 px-2 py-1 rounded-md text-[11px] hover:bg-white`}
            style={{ fontWeight: 500, color: "#7c3aed" }}
          >
            <Eye className="w-3 h-3" />
            {"החזר"}
          </button>
          <button
            type="button"
            onClick={() => onDelete(item.path)}
            className={`${btnReset} p-1 rounded hover:bg-red-50`}
            title={"מחק לצמיתות"}
            style={{ color: "#d1d5db" }}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ) : (
        /* ── Visible item: full row with drag handle + arrows + inline edit ── */
        <div
          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border bg-white ${showDropLine ? "border-purple-300 bg-purple-50/30" : "border-gray-200"}`}
        >
          {/* Drag handle */}
          <div
            ref={drag}
            className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-100"
            title={"גררו לשינוי סדר"}
          >
            <GripVertical className="w-4 h-4 shrink-0" style={{ color: "#9ca3af" }} />
          </div>

          {/* Type badge */}
          <span
            className="shrink-0 text-[10px] px-1.5 py-0.5 rounded"
            style={{
              fontWeight: 600,
              background: item.isDefault ? "#f3f4f6" : "#ede9fe",
              color: item.isDefault ? "#6b7280" : "#7c3aed",
            }}
          >
            {item.isDefault
              ? "ברירת מחדל"
              : "מותאם"}
          </span>

          {/* Label — inline editable */}
          <InlineEditableLabel
            value={item.label}
            onSave={(newLabel) => onUpdateLabel(item.path, newLabel)}
          />

          {/* Path — clickable to navigate */}
          <button
            type="button"
            onClick={() => onNavigateTo(item.path)}
            className={`${btnReset} text-xs shrink-0 hover:underline`}
            dir="ltr"
            style={{ color: "#3b82f6", fontWeight: 500 }}
            title={"עבור לעמוד"}
          >
            {item.path} ↗
          </button>

          {/* Arrow reorder buttons */}
          <div className="flex items-center gap-0 shrink-0">
            {!isFirst && (
              <button
                type="button"
                onClick={() => onReorder(item.path, "up")}
                className={`${btnReset} p-1 rounded-md hover:bg-gray-100`}
                title={"העלה"}
                style={{ color: "#9ca3af" }}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
            )}
            {!isLast && (
              <button
                type="button"
                onClick={() => onReorder(item.path, "down")}
                className={`${btnReset} p-1 rounded-md hover:bg-gray-100`}
                title={"הורדה"}
                style={{ color: "#9ca3af" }}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Add child button */}
          <button
            type="button"
            onClick={() => onStartAddChild(item.path)}
            className={`${btnReset} p-1.5 rounded-md hover:bg-purple-50`}
            title={"הוספת תת-עמוד"}
            style={{ color: "#a78bfa" }}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>

          {/* Toggle hide */}
          <button
            type="button"
            onClick={() => onToggleHidden(item.path)}
            className={`${btnReset} p-1.5 rounded-md hover:bg-gray-100`}
            title={"הסתר מהתפריט"}
            style={{ color: "#9ca3af" }}
          >
            <Eye className="w-3.5 h-3.5" />
          </button>

          {/* Delete — all items */}
          <button
            type="button"
            onClick={() => onDelete(item.path)}
            className={`${btnReset} p-1.5 rounded-md hover:bg-red-50`}
            title={"מחק עמוד"}
            style={{ color: "#ef4444" }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Children rows (indented) — only for visible items ── */}
      {!item.isHidden && hasChildren && (
        <div className="mr-6 border-r-2 border-purple-100 space-y-1 py-1">
          {item.children!.map((child) => {
            /* Is this child a custom item? */
            const childCustomId = customItems.find(
              (ci) => ci.path === child.path && ci.parentPath === item.path
            )?.id;

            return (
              <div
                key={child.path}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50/80 mr-1"
              >
                <CornerDownRight
                  className="w-3 h-3 shrink-0"
                  style={{ color: "#c4b5fd" }}
                />
                {/* Child label — inline editable */}
                <InlineEditableLabel
                  value={child.label}
                  onSave={(newLabel) => onUpdateChildLabel(child.path, item.path, newLabel)}
                />
                <span className="text-[11px] text-gray-400 shrink-0" dir="ltr">
                  {child.path}
                </span>
                {childCustomId ? (
                  <button
                    type="button"
                    onClick={() => onRemove(childCustomId)}
                    className={`${btnReset} p-1 rounded hover:bg-red-50`}
                    title={"מחק"}
                    style={{ color: "#ef4444" }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                ) : (
                  <>
                    <span
                      className="text-[9px] px-1 py-0.5 rounded"
                      style={{
                        fontWeight: 600,
                        background: "#f3f4f6",
                        color: "#9ca3af",
                      }}
                    >
                      {"ברירת מחדל"}
                    </span>
                    <button
                      type="button"
                      onClick={() => onDelete(child.path)}
                      className={`${btnReset} p-1 rounded hover:bg-red-50`}
                      title={"מחק תת-עמוד"}
                      style={{ color: "#ef4444" }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}